import { DatabaseColumnOption } from '../models/database-column-selection.types';
import { DatabaseSortDirection } from '../models/database-list.types';

export interface DatabaseRemovedColumn<TFilterKey extends string, TSortField extends string> {
	filterKey?: TFilterKey;
	sortField?: TSortField;
}

export interface DatabaseListResetState<TFilterKey extends string, TSortField extends string> {
	filterValues: Record<TFilterKey, string[]>;
	filterSearch: Record<TFilterKey, string>;
	sortField: TSortField | null;
	sortDirection: DatabaseSortDirection;
}

/** Clears filters of removed columns and falls back to the default sort when the sorted column was removed. */
export function resetStateForRemovedColumns<TFilterKey extends string, TSortField extends string>(
	state: DatabaseListResetState<TFilterKey, TSortField>,
	removedColumns: readonly DatabaseRemovedColumn<TFilterKey, TSortField>[],
	defaultSortDirection: DatabaseSortDirection,
): DatabaseListResetState<TFilterKey, TSortField> & { resetSort: boolean } {
	const filterValues = { ...state.filterValues };
	const filterSearch = { ...state.filterSearch };
	for (const column of removedColumns) {
		if (!column.filterKey) continue;
		filterValues[column.filterKey] = [];
		filterSearch[column.filterKey] = '';
	}
	const resetSort = removedColumns.some((column) => column.sortField === state.sortField);
	return {
		filterValues,
		filterSearch,
		sortField: resetSort ? null : state.sortField,
		sortDirection: resetSort ? defaultSortDirection : state.sortDirection,
		resetSort,
	};
}

export function orderedColumnIds(options: readonly DatabaseColumnOption[], selectedIds: readonly string[]) {
	const selected = new Set(selectedIds);
	return options.filter((option) => selected.has(option.schemaId)).map((option) => option.schemaId);
}

export function reconcileColumnIds(
	options: readonly DatabaseColumnOption[],
	selectedIds: readonly string[],
	defaultIds: readonly string[],
) {
	const selected = orderedColumnIds(options, selectedIds);
	if (selected.length > 0) return selected;
	return orderedColumnIds(options, defaultIds);
}

export function areColumnIdsEqual(first: readonly string[], second: readonly string[]) {
	return first.length === second.length && first.every((value, index) => value === second[index]);
}

export function areColumnSelectionsEqual(
	options: readonly DatabaseColumnOption[],
	first: readonly string[],
	second: readonly string[],
) {
	return areColumnIdsEqual(orderedColumnIds(options, first), orderedColumnIds(options, second));
}
